/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useTranslate } from "@adapptio/lib-components";
import React, { useCallback, useRef } from "react";
import { termsEditorCommon } from "../../terms/common";
import { TDialogComponent } from "../Dialog";
import { DialogHeader } from "../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../ui/components/Dialogs/DialogPanel";
import { InputDialog, InputDialog_Params } from "./InputDialog";
import { FieldText } from "../../ui/components/Fields/FieldText";
import { Typography } from "../../ui/components/Typography";
import { DialogContent } from "../../ui/components/Dialogs/DialogContent";
import { useEnhancedEffect, useFieldState } from "../../ui/hooks";
import { DialogButtons } from "../../ui/components/Dialogs/DialogButtons";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../ui/components/ActionButton";
import { Form } from "../../ui/components/Form";

const defaultParams: InputDialog_Params = {
	title: termsEditorCommon.dialog.input.title,
	description: termsEditorCommon.dialog.input.description,
	confirmLabel: termsEditorCommon.dialog.input.buttonConfirm,
	confirmStyle: ACTION_BUTTON_STYLE.PRIMARY,
	cancelLabel: termsEditorCommon.dialog.input.buttonCancel,
	cancelStyle: ACTION_BUTTON_STYLE.DEFAULT
};

export const InputDialogComponent: TDialogComponent<InputDialog> = ({ dialog }) => {
	const inputRef = useRef<HTMLInputElement>();
	const t = useTranslate();
	const dialogParams = dialog.getParams();
	const params = {
		...defaultParams,
		...dialogParams
	};
	const defaultValue = params?.defaultValue ?? "";
	const validators = params?.validators ?? [];

	const translations = {
		title: t("editor", params.title),
		description: t("editor", params.description, { defaultValue }),
		actions: {
			confirm: t("editor", params.confirmLabel),
			cancel: t("editor", params.cancelLabel)
		}
	};

	const baseClass = BEM("edt-module-core-input-dialog");

	const [ fieldState, setFieldState ] = useFieldState(
		{
			value: {
				defaultValue,
				validators
			}
		},
		[ dialog, validators, defaultValue ]
	);

	const handleClose = useCallback(() => {
		dialog.close({
			value: null
		});
	}, [ dialog ]);

	const handleRename = useCallback(() => {
		const value = fieldState.value.value;

		dialog.close({
			value
		});
	}, [ dialog, fieldState ]);

	useEnhancedEffect(() => {
		if (inputRef.current && document.activeElement === inputRef.current) {
			inputRef.current.setSelectionRange(0, inputRef.current.value?.length);
		}
	}, []);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={handleRename} noValidate>
				<DialogHeader title={translations.title} icon="mdi/pencil" onClose={handleClose} />
				<DialogContent>
					<label className={baseClass.element("field-row").toClassName()}>
						<Typography className={baseClass.element("field-label").toClassName()}>
							{translations.description}
						</Typography>
						<FieldText
							className={baseClass.element("field-input").toClassName()}
							value={fieldState.value.value}
							validationErrors={fieldState.value.validationErrors}
							onChange={setFieldState.value}
							autoFocus
							grow
							inputRef={inputRef}
						/>
					</label>
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label={translations.actions.cancel}
						icon="mdi/close"
						type="button"
						style={params.cancelStyle}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
					/>
					<ActionButton
						label={translations.actions.confirm}
						icon="mdi/check"
						type="submit"
						style={params.confirmStyle}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleRename}
						disabled={
							fieldState.value.validationErrors.length > 0 ||
							fieldState.value.value === "" ||
							fieldState.value.pristine
						}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
